import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import MeaButton from "../../../../components/MeaButton";
import MeaStudentStatus2 from "../../../../components/MeaStudentStatus2";
import studentsAPI from "../../../../services/students";
import { useAuthContext } from "../../../../hooks/useAuthContext";

const STATUS_TARGETS = {
  NEW: { label: "Nouvelle candidature", icon: "pi pi-inbox", description: "Repasser le dossier en nouvelle candidature.", title: "Remettre en candidature" },
  PREREGISTRED: { label: "Pré-inscription", icon: "pi pi-user-plus", description: "Pré-inscrire l'élève dans cette formation.", title: "Pré-inscrire l'élève" },
  ENROLLED: { label: "Confirmer l'inscription", icon: "pi pi-check-circle", description: "Valider définitivement l'inscription de l'élève.", title: "Confirmer l'inscription" },
  WAITING_LIST: { label: "Liste d'attente", icon: "pi pi-hourglass", description: "Placer l'élève en liste d'attente.", title: "Mettre en liste d'attente" },
  UNREGISTRED: { label: "Désinscription", icon: "pi pi-user-minus", description: "Désinscrire l'élève de cette formation.", title: "Désinscrire l'élève" },
};

// Statuses for which the backend stores a free text detail.
const STATUS_DETAILS = {
  ENROLLED: { label: "Détails de la confirmation (facultatif)", placeholder: "Ex. dossier complet, pièces reçues…" },
  UNREGISTRED: { label: "Motif de la désinscription (facultatif)", placeholder: "Ex. déménagement, changement d'école…" },
  WAITING_LIST: { label: "Détails sur la mise en liste d'attente (facultatif)", placeholder: "Ex. classe complète, en attente d'une place…" },
};

// Mirrors the backend rules (Student.CanUpdateStatus).
const STATUS_TRANSITIONS = {
  NEW: ["PREREGISTRED", "WAITING_LIST"],
  PREREGISTRED: ["ENROLLED", "NEW", "WAITING_LIST", "UNREGISTRED"],
  WAITING_LIST: ["NEW", "UNREGISTRED"],
  ENROLLED: ["UNREGISTRED"],
  UNREGISTRED: ["NEW", "WAITING_LIST"],
};

export const getAllowedStudentStatusTransitions = (status) =>
  (STATUS_TRANSITIONS[status] || []).map((value) => ({ value, ...STATUS_TARGETS[value] }));

const ChangeStudentStatusModal = forwardRef(({ student, schoolId, trainingId }, ref) => {
  const { currentMembership } = useAuthContext();
  const toast = useRef(null);
  const [visible, setVisible] = useState(false);
  const [newStatus, setNewStatus] = useState(null);
  const [details, setDetails] = useState("");
  const [lockedStatus, setLockedStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [callbackFn, setCallbackFn] = useState(() => () => {});

  const allowedOptions = getAllowedStudentStatusTransitions(student?.status);
  // When opened for a given action, only that transition is offered.
  const options = lockedStatus ? allowedOptions.filter((option) => option.value === lockedStatus) : allowedOptions;
  const header = lockedStatus && STATUS_TARGETS[lockedStatus] ? STATUS_TARGETS[lockedStatus].title : "Changer le statut de l'élève";
  const detailsConfig = STATUS_DETAILS[newStatus];

  useImperativeHandle(ref, () => ({
    show: (callback, { status } = {}) => {
      const target = status && (STATUS_TRANSITIONS[student?.status] || []).includes(status) ? status : null;
      setLockedStatus(status || null);
      setNewStatus(target);
      setDetails("");
      setCallbackFn(() => callback);
      setVisible(true);
    },
    hide: () => setVisible(false),
  }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const trimmedDetails = details.trim() || null;
      if (newStatus === "ENROLLED") {
        await studentsAPI.UpdateStatus(schoolId, trainingId, "", student?.id, newStatus, currentMembership?.id, trimmedDetails);
      } else if (newStatus === "UNREGISTRED") {
        await studentsAPI.UpdateStatus(schoolId, trainingId, "", student?.id, newStatus, undefined, undefined, trimmedDetails);
      } else if (newStatus === "WAITING_LIST") {
        await studentsAPI.UpdateStatus(schoolId, trainingId, "", student?.id, newStatus, undefined, undefined, undefined, trimmedDetails);
      } else {
        await studentsAPI.UpdateStatus(schoolId, trainingId, "", student?.id, newStatus);
      }
      toast.current?.show({ severity: "success", summary: "Succès", life: 1500, detail: "Statut de l'élève mis à jour." });
      setVisible(false);
      callbackFn?.();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Erreur", detail: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} position="bottom-center" />
      <Dialog
        header={header}
        visible={visible}
        style={{ width: "35vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        modal
        onHide={() => !loading && setVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <MeaButton label="Annuler" icon="pi pi-times" className="p-button-text" disabled={loading} onClick={() => setVisible(false)} />
            {options.length > 0 && (
              <MeaButton
                label={lockedStatus ? "Confirmer" : "Enregistrer"}
                icon="pi pi-save"
                severity={newStatus === "UNREGISTRED" ? "danger" : undefined}
                loading={loading}
                disabled={!newStatus}
                onClick={handleSave}
              />
            )}
          </div>
        }
      >
        <div className="flex align-items-center gap-2 mb-3 text-sm">
          <span className="text-500">Statut actuel :</span>
          <MeaStudentStatus2 student={student} />
        </div>
        {options.length === 0 && (
          <Message severity="info" className="w-full justify-content-start" text="Aucun changement de statut n'est possible depuis le statut actuel." />
        )}
        <div className="flex flex-column gap-2">
          {options.map((option) => {
            const inputId = `student-status-${option.value}`;
            return (
              <div
                key={option.value}
                className={`flex align-items-start gap-3 p-3 border-1 border-round cursor-pointer ${newStatus === option.value ? "border-primary surface-100" : "surface-border"}`}
                onClick={() => setNewStatus(option.value)}
              >
                <RadioButton inputId={inputId} name="student-status" value={option.value} checked={newStatus === option.value} onChange={(e) => setNewStatus(e.value)} />
                <label htmlFor={inputId} className="flex-1 cursor-pointer">
                  <span className="font-medium"><i className={`${option.icon} mr-2`} />{option.label}</span>
                  <small className="block text-500 mt-1">{option.description}</small>
                </label>
              </div>
            );
          })}
        </div>
        {detailsConfig && (
          <div className="flex flex-column gap-2 mt-3">
            <label htmlFor="student-status-details" className="font-medium">{detailsConfig.label}</label>
            <InputTextarea
              id="student-status-details"
              rows={3}
              autoResize
              maxLength={1000}
              placeholder={detailsConfig.placeholder}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="w-full"
            />
          </div>
        )}
        {newStatus === "UNREGISTRED" && (
          <Message severity="warn" className="w-full justify-content-start mt-3" text="L'élève sera désinscrit de cette formation." />
        )}
      </Dialog>
    </>
  );
});

export default ChangeStudentStatusModal;
